/**
 * The scan as one file somebody else can open.
 *
 * The CSV is the data; this is the reading. A supervisor asking "is the
 * cohort usable" wants the sentence and the picture, not ninety-six rows to
 * sort — and they want it without installing anything, which is why it is a
 * single self-contained HTML file: the charts are SVG already, the styles are
 * inline, and there is nothing to fetch. It opens in any browser, on any
 * machine, in five years.
 *
 * Everything in it is a measurement the scan made. Nothing is summarised into
 * a grade, and there is no score: "91 of 96 can run, 1 will fail, 3 have files
 * the pipeline will not find" is what was observed, and it is more use than a
 * letter would be.
 */

import type { CohortScan, Distribution, FileIntegrity } from "@/ipc/contract";

/** HTML-escape. The inputs are filenames and a reader's own words. */
function esc(value: unknown): string {
  return String(value ?? "").replace(
    /[&<>"']/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!,
  );
}

function formatValue(value: number, unit: string): string {
  if (unit === "bytes") {
    if (value >= 1e9) return `${(value / 1e9).toFixed(1)} GB`;
    if (value >= 1e6) return `${(value / 1e6).toFixed(1)} MB`;
    return `${(value / 1e3).toFixed(0)} kB`;
  }
  return Number.isInteger(value) ? value.toLocaleString() : value.toPrecision(4);
}

/** A strip plot as standalone SVG, the same shape the app draws. */
function stripSvg(d: Distribution): string {
  const width = 640;
  const span = d.max - d.min || 1;
  const x = (v: number) => 8 + ((v - d.min) / span) * (width - 16);
  const outliers = new Set(d.outliers.map((o) => o.subjectId));
  const dots = d.points
    .map((p) => {
      const out = outliers.has(p.subjectId);
      return `<circle cx="${x(p.value).toFixed(1)}" cy="16" r="${out ? 3.5 : 2.5}" fill="${
        out ? "#f59e0b" : "#a3a3a3"
      }"><title>${esc(p.subjectId)}: ${esc(formatValue(p.value, d.unit))}</title></circle>`;
    })
    .join("");
  return `<svg width="${width}" height="32" role="img" aria-label="${esc(d.label)}">
  <rect x="${x(d.q1).toFixed(1)}" y="10" width="${Math.max(x(d.q3) - x(d.q1), 1).toFixed(1)}" height="12" rx="2" fill="#eff6ff"/>
  <line x1="8" x2="${width - 8}" y1="16" y2="16" stroke="#e5e7eb"/>
  <line x1="${x(d.median).toFixed(1)}" x2="${x(d.median).toFixed(1)}" y1="8" y2="24" stroke="#2563eb" stroke-width="1.5"/>
  ${dots}
</svg>`;
}

/** Subject × role, one cell each. */
function matrixSvg(scan: CohortScan, roles: string[]): string {
  const cell = 11;
  const gap = 2;
  const labelWidth = 116;
  const header = 54;
  const width = labelWidth + roles.length * (cell + gap);
  const height = header + scan.subjects.length * (cell + gap);

  const common = new Map(
    roles.map((role) => {
      const g = scan.consistency.find((c) => c.role === role)?.common ?? null;
      return [role, g ? `${g.dim.join("×")}·${g.voxelUm.join("×")}·${g.orientation}·${g.datatype}` : null];
    }),
  );

  const heads = roles
    .map((role, c) => {
      const cx = labelWidth + c * (cell + gap) + cell / 2;
      return `<text x="${cx}" y="${header - 8}" transform="rotate(-45 ${cx} ${header - 8})" font-size="9" fill="#6b7280">${esc(role)}</text>`;
    })
    .join("");

  const rows = scan.subjects
    .map((s, r) => {
      const y = header + r * (cell + gap);
      const cells = roles
        .map((role, c) => {
          const image = s.images.find((i) => i.role === role);
          let fill = "#e5e5e5";
          let word = "missing";
          if (image) {
            const want = common.get(role);
            const shape = image.signature
              ? `${image.signature.dim.join("×")}·${image.signature.voxelUm.join("×")}·${image.signature.orientation}·${image.signature.datatype}`
              : null;
            const differs = want && shape && shape !== want;
            fill = differs ? "#f59e0b" : "#10b981";
            word = differs ? "geometry differs from the cohort" : "present";
          }
          return `<rect x="${labelWidth + c * (cell + gap)}" y="${y}" width="${cell}" height="${cell}" rx="1.5" fill="${fill}"><title>${esc(s.subjectId)} · ${esc(role)}: ${esc(word)}</title></rect>`;
        })
        .join("");
      return `<text x="${labelWidth - 6}" y="${y + cell - 2}" text-anchor="end" font-size="9" font-family="ui-monospace,monospace" fill="#737373">${esc(s.subjectId)}</text>${cells}`;
    })
    .join("");

  return `<svg width="${width}" height="${height}" role="img" aria-label="Completeness">${heads}${rows}</svg>`;
}

function headline(scan: CohortScan): string {
  if (scan.withErrors > 0) {
    return `${scan.dwiReadyCount} of ${scan.subjectCount} subjects can run. ${scan.withErrors} ${
      scan.withErrors === 1 ? "has a problem that would" : "have problems that would"
    } make the run fail.`;
  }
  if (scan.withWarnings > 0) {
    return `All ${scan.subjectCount} subjects can run. ${scan.withWarnings} ${
      scan.withWarnings === 1 ? "has something" : "have something"
    } worth looking at first.`;
  }
  return `All ${scan.subjectCount} subjects are complete and consistent.`;
}

export interface ReportInput {
  scan: CohortScan;
  roles: string[];
  /** Absent when the deep check has not been run — which the report says. */
  integrity?: { findings: FileIntegrity[]; intensity: Distribution[]; total: number } | null;
  appVersion?: string;
}

export function buildReport({ scan, roles, integrity, appVersion }: ReportInput): string {
  const when = new Date(scan.scannedIso);
  const coverage = scan.coverage
    .map(
      (c) => `<tr><td>${esc(c.role)}</td><td class="n">${c.present} / ${scan.subjectCount}</td>
        <td class="mono warn">${esc(c.missingSubjectIds.slice(0, 12).join(", "))}${
          c.missingSubjectIds.length > 12 ? ` and ${c.missingSubjectIds.length - 12} more` : ""
        }</td></tr>`,
    )
    .join("");

  const unreachable = scan.naming.unreachable.length
    ? `<h2>Files the pipeline will not find</h2>
       <p>OpenADS locates its inputs by filename. These exist and will not be found as they are named.</p>
       <table><tr><th>Subject</th><th>Role</th><th>File</th><th>Would have to be called</th></tr>
       ${scan.naming.unreachable
         .map(
           (u) =>
             `<tr><td class="mono">${esc(u.subjectId)}</td><td>${esc(u.role)}</td><td class="mono">${esc(u.fileName)}</td><td class="mono">${esc(u.expected)}</td></tr>`,
         )
         .join("")}</table>`
    : "";

  const spreads = [...scan.distributions, ...(integrity?.intensity ?? [])]
    .map(
      (d) => `<div class="spread"><div class="spread-head"><span>${esc(d.label)}</span>
        <span class="n">median ${esc(formatValue(d.median, d.unit))} · ${esc(formatValue(d.min, d.unit))} – ${esc(formatValue(d.max, d.unit))}</span></div>
        ${stripSvg(d)}
        ${
          d.outliers.length
            ? `<p class="warn">${d.outliers
                .slice(0, 8)
                .map((o) => `${esc(o.subjectId)} (${esc(formatValue(o.value, d.unit))})`)
                .join(", ")}</p>`
            : ""
        }</div>`,
    )
    .join("");

  const inside = integrity
    ? integrity.findings.length === 0
      ? `<p class="ok">All ${integrity.total} files were opened and read to the end. Nothing wrong inside any of them.</p>`
      : `<table><tr><th>File</th><th>What is wrong</th></tr>${integrity.findings
          .map(
            (f) =>
              `<tr><td class="mono">${esc(f.fileName)}</td><td>${f.problems
                .map((p) => `<div class="${p.severity === "error" ? "err" : "warn"}">${esc(p.message)}</div>`)
                .join("")}</td></tr>`,
          )
          .join("")}</table>`
    : `<p class="warn">Not checked. Everything in this report was read from folder listings and
       headers — a file can pass all of it and still be truncated, empty, or full of NaNs.</p>`;

  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8">
<title>Data scan — ${esc(scan.root.split(/[\\/]/).filter(Boolean).pop() ?? "")}</title>
<style>
  :root { color-scheme: light }
  body { margin: 0; padding: 40px; background: #fff; color: #1f2328;
         font: 13px/1.6 "Segoe UI", system-ui, -apple-system, sans-serif; }
  main { max-width: 900px; margin: 0 auto }
  h1 { font-size: 22px; margin: 0 0 4px; letter-spacing: -0.01em }
  h2 { font-size: 15px; margin: 32px 0 6px }
  p { margin: 6px 0 }
  .path { font-family: ui-monospace, monospace; font-size: 11px; color: #9ca3af }
  .lede { padding: 12px 16px; border-radius: 8px; font-size: 14px; margin: 16px 0 }
  .lede.bad { background: #fef2f2; border: 1px solid #fecaca }
  .lede.warn { background: #fffbeb; border: 1px solid #fde68a }
  .lede.ok { background: #ecfdf5; border: 1px solid #a7f3d0 }
  table { border-collapse: collapse; width: 100%; font-size: 12px; margin-top: 6px }
  th { text-align: left; font-weight: 600; color: #4b5563; border-bottom: 1px solid #e5e7eb; padding: 4px 8px }
  td { border-bottom: 1px solid #f3f4f6; padding: 4px 8px; vertical-align: top }
  .mono { font-family: ui-monospace, monospace; font-size: 11px }
  .n { text-align: right; font-variant-numeric: tabular-nums }
  .warn { color: #b45309 } .err { color: #b91c1c } .ok { color: #047857 }
  .spread { margin: 10px 0 }
  .spread-head { display: flex; justify-content: space-between; font-size: 12px; color: #4b5563 }
  .legend { display: flex; gap: 16px; font-size: 11px; color: #6b7280; margin-top: 8px }
  .sw { display: inline-block; width: 10px; height: 10px; border-radius: 2px; margin-right: 5px }
  footer { margin-top: 40px; padding-top: 12px; border-top: 1px solid #e5e7eb;
           font-size: 11px; color: #9ca3af }
</style></head>
<body><main>
<h1>Data scan</h1>
<p class="path">${esc(scan.root)}</p>
<p class="path">Read ${esc(when.toLocaleString())}</p>

<p class="lede ${scan.withErrors ? "bad" : scan.withWarnings ? "warn" : "ok"}">${esc(headline(scan))}</p>
<p>${esc(scan.verdict.evidence.join(" "))}</p>

<h2>What each subject has</h2>
<table><tr><th>Role</th><th class="n">Present</th><th>Missing from</th></tr>${coverage}</table>

<h2>Completeness</h2>
${matrixSvg(scan, roles)}
<div class="legend">
  <span><span class="sw" style="background:#10b981"></span>present</span>
  <span><span class="sw" style="background:#f59e0b"></span>geometry differs from the cohort</span>
  <span><span class="sw" style="background:#e5e5e5"></span>missing</span>
</div>

${unreachable}

${spreads ? `<h2>How much of it</h2><p>Every subject is a point; the ones far from the middle are named.</p>${spreads}` : ""}

<h2>What is inside the files</h2>
${inside}

<footer>
  ADS Desk${appVersion ? ` ${esc(appVersion)}` : ""} · OpenADS · DAGMNet · SynthStrip · ANTs · JHU-ICBM.
  Every figure here was measured from the files named above. For research and review, not a diagnosis.
</footer>
</main></body></html>`;
}

/** `cohort_scan_20260920.html` */
export function reportFileName(scan: CohortScan): string {
  const folder = scan.root.split(/[\\/]/).filter(Boolean).pop() ?? "dataset";
  const day = (scan.scannedIso.split("T")[0] ?? "").replace(/-/g, "");
  return `${folder}_scan_${day || "report"}.html`;
}
